/**
 * 🆔 Booking ID Utilities
 *
 * Format: HVL-YYMM-XXXXXX
 *   - HVL  → Havlaa prefix
 *   - YY   → آخر رقمين من السنة
 *   - MM   → الشهر
 *   - XXXXXX → 6 أحرف random
 *
 * مثال: HVL-2605-AB1C23
 */

const PREFIX = 'HVL';
const ALPHA = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // بدون 0/1/I/O للوضوح

/**
 * 🎲 توليد Booking displayId جديد
 *
 * 🛡️ (L-1 fix) cryptographically secure random
 *   كان Math.random() — entropy = 32^6 = ~1B (قابل للتنبؤ).
 *   الآن: crypto.getRandomValues — entropy حقيقي.
 *
 *   ملاحظة: هذا displayId مش security-critical (الـ docId الـ Firestore هو الـ ID الفعلي)،
 *   لكن randomness أقوى أفضل (يمنع enumeration attacks).
 */
export function generateBookingDisplayId(at: Date = new Date()): string {
  const yy = at.getFullYear() % 100;
  const mm = at.getMonth() + 1;
  const yymm = `${String(yy).padStart(2, '0')}${String(mm).padStart(2, '0')}`;

  // crypto-safe random (Browser + Node.js modern)
  let random = '';

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(6);
    crypto.getRandomValues(bytes);
    for (let i = 0; i < 6; i++) {
      random += ALPHA[bytes[i]! % ALPHA.length];
    }
  } else {
    // Fallback (لا يفترض يحصل في environments الحديثة)
    for (let i = 0; i < 6; i++) {
      random += ALPHA[Math.floor(Math.random() * ALPHA.length)];
    }
  }

  return `${PREFIX}-${yymm}-${random}`;
}

/**
 * 📝 تنسيق الـ booking ID للعرض
 *
 * الأولوية:
 *   1. لو فيه displayId جاهز (HVL-...) → نعرضه مباشرة
 *   2. لو الـ id يبدأ بـ HVL- → نعرضه
 *   3. لو الـ id يبدأ بـ seed_ → نولّد displayId من timestamp
 *   4. الباقي → نولّد short hash
 */
export function formatBookingDisplayId(
  rawId: string,
  displayId?: string | null | undefined
): string {
  // 1. لو في displayId محفوظ
  if (displayId && displayId.startsWith(PREFIX)) {
    return displayId;
  }

  // 2. لو الـ id نفسه HVL-format
  if (rawId.startsWith(`${PREFIX}-`)) {
    return rawId;
  }

  // 3. seed_ID — نولّد من الـ timestamp
  if (rawId.startsWith('seed_')) {
    const timestampMatch = rawId.match(/seed_(\d+)/);
    if (timestampMatch?.[1]) {
      const ts = parseInt(timestampMatch[1], 10);
      const date = new Date(ts);
      const yy = date.getFullYear() % 100;
      const mm = date.getMonth() + 1;
      const yymm = `${String(yy).padStart(2, '0')}${String(mm).padStart(2, '0')}`;
      const short = rawId.slice(-6).toUpperCase();
      return `${PREFIX}-${yymm}-${short}`;
    }
  }

  // 4. fallback — استخدم آخر 6 أحرف
  const short = rawId.slice(-6).toUpperCase();
  const now = new Date();
  const yy = now.getFullYear() % 100;
  const mm = now.getMonth() + 1;
  const yymm = `${String(yy).padStart(2, '0')}${String(mm).padStart(2, '0')}`;
  return `${PREFIX}-${yymm}-${short}`;
}

/**
 * ✂️ نسخة مختصرة للعرض في الجداول (آخر 6 أحرف فقط)
 */
export function shortBookingId(displayId: string): string {
  // HVL-2605-AB1C23 → AB1C23
  const parts = displayId.split('-');
  return parts[parts.length - 1] ?? displayId;
}
